import React, { memo, useCallback, useMemo } from "react";
import { onInputKeyDown, SEARCH_VALUE_ID } from "./utils";
import { Button, Icon } from "../../components/tailwind";
import { SEARCH_INPUT_ID } from "./utils";
import isEqual from "lodash/isEqual";
import { useNoTranslateCells } from "./useNoTranslateCells";
import { ExcelFileViewerMode } from "./types";
type ActionProps = {
  noTranslateHandler: ReturnType<
    | ReturnType<typeof useNoTranslateCells>["getRow"]
    | ReturnType<typeof useNoTranslateCells>["getColumn"]
    | ReturnType<typeof useNoTranslateCells>["getCell"]
  >;
  remainFocus: (selector: string) => void;
  selector: string;
  canBeDisabled?: boolean;
};
const Actions: React.FC<ActionProps> = ({
  noTranslateHandler,
  remainFocus,
  selector,
  canBeDisabled = true,
}) => {
  return (
    <Button
      disabled={canBeDisabled && noTranslateHandler?.info?.locked}
      size="sm"
      compact="very"
      type="button"
      onClick={() => {
        if (noTranslateHandler?.marked) {
          noTranslateHandler.unmark();
        } else {
          noTranslateHandler.mark();
        }
        remainFocus(selector);
      }}
    >
      {noTranslateHandler?.marked ? (
        <Icon name="lock_open" />
      ) : (
        <Icon name="lock" />
      )}
    </Button>
  );
};

type Props = {
  focusedCell: {
    row: number;
    column: number;
  };
  noTranslateCells: ReturnType<typeof useNoTranslateCells>;
  mode: ExcelFileViewerMode;
};
const ToolbarComponent: React.FC<Props> = ({
  focusedCell,
  noTranslateCells,
  mode,
}) => {
  const remainFocus = useCallback(
    (selector: string) => {
      const focusedCellElement = document.querySelector<HTMLTableCellElement>(
        selector
      );
      if (focusedCellElement) {
        focusedCellElement.focus();
      }
    },
    [focusedCell]
  );

  const actionType = useMemo((): "cell" | "column" | "row" | undefined => {
    if (!!focusedCell?.row && !!focusedCell?.column) {
      return "cell";
    }
    if (!!focusedCell?.row && !focusedCell?.column && focusedCell?.row !== 1) {
      return "row";
    }
    if (!focusedCell?.row && !!focusedCell?.column && focusedCell?.row !== 1) {
      return "column";
    }
  }, [focusedCell]);

  const actionProps = useMemo(() => {
    switch (actionType) {
      case "cell":
        return {
          noTranslateHandler: noTranslateCells.getCell(
            focusedCell?.row,
            focusedCell?.column
          ),
          remainFocus,
          selector: `[data-row='${focusedCell?.row}'][data-column='${focusedCell?.column}']`,
        };
      case "row":
        return {
          noTranslateHandler: noTranslateCells.getRow(focusedCell?.row),
          remainFocus,
          selector: `[data-number='${focusedCell?.row}']`,
        };
      case "column":
        return {
          noTranslateHandler: noTranslateCells.getColumn(focusedCell?.column),
          remainFocus,
          selector: `[data-letter='${focusedCell?.column}']`,
          canBeDisabled: false,
        };
    }
  }, [focusedCell, noTranslateCells, remainFocus, actionType]);

  const showNoActions =
    isNaN(focusedCell?.row) ||
    isNaN(focusedCell?.column) ||
    focusedCell?.row === 1 ||
    mode === "view";
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-2">
        <input
          type="text"
          id={SEARCH_INPUT_ID}
          className="txu-input tw-w-24"
          placeholder="A1"
          onKeyDown={onInputKeyDown}
        />
        <span
          id={SEARCH_VALUE_ID}
          className="tw-text-xs tw-text-gray-600"
        ></span>
      </div>
      <div
        className={`tw-flex tw-items-center tw-gap-2 tw-text-sm ${
          showNoActions ? "tw-invisible" : ""
        }`}
      >
        <Actions {...actionProps} />
      </div>
    </div>
  );
};

export const Toolbar = memo(ToolbarComponent, (prevProps, nextProps) => {
  return (
    isEqual(prevProps.focusedCell, nextProps.focusedCell) &&
    prevProps.noTranslateCells === nextProps.noTranslateCells &&
    prevProps.mode === nextProps.mode
  );
});
